<!--
  The corpus browser: all 3,588 records, filtered however you like.

  This is where the work queue's filter controls went, and where they belong. The browser
  answers "show me the records like this" — including the ones no task can reach: zero imagery,
  a shared siglum, a catalog block. Those are often exactly what you came to look at, and the
  old queue's `queue-eligible only` default hid them.

  **No progress meters here, deliberately.** A count over an arbitrary filter is not progress
  toward anything: filter to `unannotated` and a scoped completion bar reads empty however much
  you do, because you filtered to the work that is not done. Progress belongs to a task and
  lives on its card. What this screen shows instead is a census — how many records match, and
  how many exist — which is a fact rather than an achievement.
-->
<script lang="ts">
  import { untrack } from 'svelte';

  import type { ImageStatus, Split } from '../lib/api/types';
  import { statusLabel } from '../lib/imageStatus';
  import { displaySiglum } from '../lib/nav';
  import { href, router } from '../lib/router.svelte';
  import { browse } from '../lib/stores/browse.svelte';
  import { stats } from '../lib/stores/stats.svelte';

  const STATUSES: ImageStatus[] = [
    'unannotated',
    'in_progress',
    'done',
    'skipped',
    'not_pictured',
  ];
  const SPLITS: Split[] = ['train', 'val', 'test'];

  /** A tri-state checkbox as two radio-ish values plus "either". `''` means no opinion. */
  const TRISTATE = [
    { value: '', label: 'either' },
    { value: 'true', label: 'yes' },
    { value: 'false', label: 'no' },
  ];

  function triValue(current: boolean | undefined): string {
    return current === undefined ? '' : String(current);
  }

  function triParse(raw: string): boolean | undefined {
    return raw === '' ? undefined : raw === 'true';
  }

  $effect(() => {
    untrack(() => {
      void browse.ensure();
      void stats.ensure();
    });
  });

  const corpusTotal = $derived(stats.data?.corpus.inscriptions ?? null);

  /**
   * Human wording for the catalog's reason codes. Missing codes fall through to the raw string.
   *
   * The code stays on screen and the sentence goes in the `title`, not the other way round: a
   * block reason is evidence, and evidence you have paraphrased is a thing somebody has to go
   * and check against the catalog before they can trust it.
   */
  const REASON_TEXT: Record<string, string> = {
    no_images: 'No image is linked to this record, so there is nothing to annotate.',
    ambiguous_images:
      'This siglum key names more than one OCIANA record, so the catalog cannot tell their images apart.',
    corpus_excluded: 'Excluded from the queue and from priors.json — see the specific reason beside this.',
    no_transliteration: 'OCIANA publishes no transliteration, so there is no expected-letter sequence.',
    zero_letter: 'The transliteration tokenises to no letters at all — usually an entire {----} lacuna.',
    scrape_doubled: 'The transliteration line is the transliteration and its translation run together.',
    disputed_alternative: 'The record carries an alternative reading, which is a bad weak label.',
    foreign_script: 'Its own apparatus identifies the text as another script.',
  };

  /**
   * The flag tags for one row, deduplicated.
   *
   * `AMJ 002` used to render four: `no transliteration`, `no_images`, `corpus_excluded` and
   * `no_transliteration` — the transliteration fact **twice**, once prettified from
   * `has_transliteration` and once raw from `corpus_exclusions`, plus `corpus_excluded`, which
   * is only the container the exclusions below it are already itemising. Three tags of signal
   * dressed as four, one of them a straight duplicate.
   *
   * So: one set, keyed on the code; the derived `no transliteration` chip is dropped in favour
   * of the catalog's own `no_transliteration`; and `corpus_excluded` is dropped whenever a
   * specific exclusion is present to explain it. Nothing is invented and nothing that is not
   * stated elsewhere on the row is removed.
   */
  function reasonTags(item: {
    queue_block_reasons: readonly string[];
    corpus_exclusions: readonly string[];
    has_transliteration: boolean;
  }): { code: string; title: string }[] {
    const codes: string[] = [...item.queue_block_reasons, ...item.corpus_exclusions];
    if (!item.has_transliteration && !codes.includes('no_transliteration')) {
      codes.push('no_transliteration');
    }
    const specific = codes.some((code) => code !== 'corpus_excluded' && REASON_TEXT[code]);
    const seen = new Set<string>();
    const out: { code: string; title: string }[] = [];
    for (const code of codes) {
      if (code === 'corpus_excluded' && specific) continue;
      if (seen.has(code)) continue;
      seen.add(code);
      out.push({ code, title: REASON_TEXT[code] ?? code });
    }
    return out;
  }
</script>

<div class="browser col">
  <div class="col" style="gap:2px">
    <h2>Corpus browser</h2>
    <p class="small muted">
      Every record OCIANA published, whether or not anything can be annotated on it. Looking for
      the next piece of work instead? That is the
      <a href={href({ name: 'queue', task: null })}>work queue</a>.
    </p>
  </div>

  <div class="panel col">
    <div class="row wrap filters">
      <label class="col small">
        Siglum prefix
        <input
          type="text"
          placeholder="AMJ"
          value={browse.filter.siglumPrefix ?? ''}
          onchange={(event) =>
            void browse.setFilter({ siglumPrefix: event.currentTarget.value || undefined })}
        />
      </label>
      <label class="col small">
        Image status
        <select
          value={browse.filter.status ?? ''}
          onchange={(event) =>
            void browse.setFilter({
              status: (event.currentTarget.value || undefined) as ImageStatus | undefined,
            })}
        >
          <option value="">any</option>
          {#each STATUSES as status (status)}
            <option value={status}>{statusLabel(status)}</option>
          {/each}
        </select>
      </label>
      <label class="col small">
        Split
        <select
          value={browse.filter.split ?? ''}
          onchange={(event) =>
            void browse.setFilter({
              split: (event.currentTarget.value || undefined) as Split | undefined,
            })}
        >
          <option value="">any</option>
          {#each SPLITS as split (split)}
            <option value={split}>{split}</option>
          {/each}
        </select>
      </label>
      <label class="col small">
        Min letters
        <input
          type="number"
          min="0"
          value={browse.filter.minLetters ?? ''}
          onchange={(event) =>
            void browse.setFilter({
              minLetters: event.currentTarget.value ? Number(event.currentTarget.value) : undefined,
            })}
        />
      </label>
      <label class="col small">
        Max letters
        <input
          type="number"
          min="0"
          value={browse.filter.maxLetters ?? ''}
          onchange={(event) =>
            void browse.setFilter({
              maxLetters: event.currentTarget.value ? Number(event.currentTarget.value) : undefined,
            })}
        />
      </label>
      <!--
        Tri-state rather than a checkbox. A checkbox can say "seed batch only" and "don't care",
        but not "everything the sampler passed over" — and `queue_eligible: no` is how you find
        the records the catalog blocked, which is a question only this screen can ask.
      -->
      <label class="col small">
        Seed batch
        <select
          value={triValue(browse.filter.seedBatch)}
          onchange={(event) =>
            void browse.setFilter({ seedBatch: triParse(event.currentTarget.value) })}
        >
          {#each TRISTATE as option (option.value)}
            <option value={option.value}>{option.label}</option>
          {/each}
        </select>
      </label>
      <label class="col small">
        Has imagery
        <select
          value={triValue(browse.filter.hasImages)}
          onchange={(event) =>
            void browse.setFilter({ hasImages: triParse(event.currentTarget.value) })}
        >
          {#each TRISTATE as option (option.value)}
            <option value={option.value}>{option.label}</option>
          {/each}
        </select>
      </label>
      <label class="col small">
        Queue-eligible
        <select
          value={triValue(browse.filter.queueEligible)}
          onchange={(event) =>
            void browse.setFilter({ queueEligible: triParse(event.currentTarget.value) })}
        >
          {#each TRISTATE as option (option.value)}
            <option value={option.value}>{option.label}</option>
          {/each}
        </select>
      </label>
      <span class="spacer"></span>
      <button onclick={() => void browse.clearFilter()} disabled={!browse.filtered}>
        Clear
      </button>
      <button onclick={() => void browse.load()} disabled={browse.loading}>Refresh</button>
    </div>

    {#if browse.problem}
      <div class="banner error small">
        <strong>Could not load the corpus</strong>
        <p>{browse.problem.message}</p>
        <p class="muted mono">code: {browse.problem.code}</p>
      </div>
    {/if}

    <table>
      <thead>
        <tr>
          <th>Siglum</th>
          <th>Letters</th>
          <th>Images</th>
          <th>Boxes</th>
          <th>Flags</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {#each browse.items as item (item.id)}
          <tr>
            <td>
              <div class="col" style="gap:0">
                <span title={item.siglum_raw}>{displaySiglum(item)}</span>
                <span class="small muted mono">{item.ociana_id ?? ''}</span>
              </div>
            </td>
            <td class="mono">{item.token_count}</td>
            <td class="mono">{item.images_done}/{item.image_count}</td>
            <td class="mono">{item.annotation_count}</td>
            <td class="small">
              {#if item.seed_batch}<span class="tag">seed</span>{/if}
              {#if item.split}<span class="tag">{item.split}</span>{/if}
              {#if item.siglum_shared_with.length}
                <span class="tag warn" title="This siglum key names more than one record">
                  shared siglum
                </span>
              {/if}
              <!-- Verbatim, in the catalog's words, and each one once. A block reason is
                   evidence, not a hint — see `reasonTags`. -->
              {#each reasonTags(item) as tag (tag.code)}
                <span class="tag warn" title={tag.title}>{tag.code}</span>
              {/each}
            </td>
            <td>
              <button
                onclick={() => router.go({ name: 'inscription', inscriptionId: item.id })}
              >
                View record
              </button>
            </td>
          </tr>
        {/each}
        {#if !browse.items.length && !browse.loading}
          <tr>
            <td colspan="6" class="muted small">
              {#if browse.filtered}
                No record matches this filter.
              {:else}
                No records are loaded. Run
                <span class="mono">python -m hismaic.db.load</span>.
              {/if}
            </td>
          </tr>
        {/if}
      </tbody>
    </table>

    <div class="row small">
      <span class="muted">
        {browse.total} record(s) match{#if corpusTotal !== null}
          of {corpusTotal} in the corpus{/if} · showing {browse.total === 0
          ? 0
          : browse.offset + 1}–{Math.min(browse.offset + browse.pageSize, browse.total)}
      </span>
      <span class="spacer"></span>
      <button
        onclick={() => void browse.goTo(browse.offset - browse.pageSize)}
        disabled={browse.offset === 0 || browse.loading}
      >
        Previous
      </button>
      <button
        onclick={() => void browse.goTo(browse.offset + browse.pageSize)}
        disabled={browse.offset + browse.pageSize >= browse.total || browse.loading}
      >
        Next
      </button>
    </div>
  </div>
</div>

<style>
  .browser {
    padding: 12px;
    gap: 12px;
    max-width: 1280px;
    margin: 0 auto;
    width: 100%;
  }

  .filters {
    align-items: flex-end;
    gap: 12px;
  }

  .filters input[type='text'],
  .filters input[type='number'] {
    width: 110px;
  }

  .tag {
    display: inline-block;
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    padding: 0 5px;
    margin-right: 4px;
    font-size: var(--text-xs);
  }

  .tag.warn {
    border-color: var(--warn);
    color: var(--warn);
  }
</style>
